import { useEffect, useState } from "react";

// Live countdown to a target ISO timestamp - purely presentational (per the
// product spec: the countdown reaching zero does NOT itself authorize
// anything, the server's own unlock sweep/gift_unlocked push and the
// existing `locked` field from every gift response remain the actual
// source of truth). Second-granularity under 24h (matches a "08:42:17"
// display), minute-granularity above it - deliberately not sub-second/
// millisecond, which the spec explicitly calls out as distracting.
function format(msRemaining: number): string {
  if (msRemaining <= 0) return "00:00:00";
  const totalSec = Math.floor(msRemaining / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (days > 0) return `${days}d ${pad(hours)}h ${pad(minutes)}m`;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export function useCountdown(targetIso: string | null): { label: string; done: boolean; msRemaining: number } {
  const target = targetIso ? Date.parse(targetIso) : NaN;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!Number.isFinite(target)) return;
    // Re-picks its own cadence each time it (re)schedules: minute-
    // granularity while more than a day remains (a HH:MM:SS-style display
    // isn't shown that far out anyway), second-granularity once under a
    // day - re-evaluated on its own tick via setTimeout's self-scheduling
    // rather than a fixed setInterval, so it naturally speeds up as the
    // target gets closer without a separate effect dependency to track.
    let id: ReturnType<typeof setTimeout>;
    function tick(): void {
      setNow(Date.now());
      const msRemaining = target - Date.now();
      const delay = msRemaining > 86400_000 ? 60_000 : 1000;
      id = setTimeout(tick, delay);
    }
    id = setTimeout(tick, 0);
    return () => clearTimeout(id);
  }, [target]);

  if (!Number.isFinite(target)) return { label: "", done: true, msRemaining: 0 };
  const msRemaining = target - now;
  return { label: format(msRemaining), done: msRemaining <= 0, msRemaining };
}
